/**
 * UTM Parameter Capture Utility
 *
 * Extracts UTM parameters from the URL and stores them in sessionStorage.
 * Handles the HashRouter edge case where UTM params appear after the `#`.
 *
 * Examples of URLs this handles:
 *   https://translayshun.com/#/?utm_source=google&utm_medium=cpc
 *   https://translayshun.com/?utm_source=google#/app
 *   https://translayshun.com/#/app?utm_source=google&utm_medium=cpc
 */

const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;

export interface UtmParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
}

const SESSION_STORAGE_KEY = 'translayshun_utm_params';

/**
 * Extract UTM parameters from the current URL.
 * Checks both the regular query string and the hash portion (for HashRouter).
 */
function extractUtmFromUrl(): UtmParams {
  const params: UtmParams = {};

  // Strategy 1: Check regular query string (before the #)
  const regularParams = new URLSearchParams(window.location.search);

  // Strategy 2: Check hash portion — HashRouter puts routes after #
  // The hash might look like: #/?utm_source=x  or  #/app?utm_source=x
  let hashParams = new URLSearchParams();
  const hash = window.location.hash;
  const hashQueryIndex = hash.indexOf('?');
  if (hashQueryIndex !== -1) {
    hashParams = new URLSearchParams(hash.substring(hashQueryIndex));
  }

  // Merge: hash params take precedence (more likely to be the actual landing URL)
  for (const key of UTM_KEYS) {
    const value = hashParams.get(key) || regularParams.get(key);
    if (value) {
      params[key] = value;
    }
  }

  return params;
}

/**
 * Returns true if the UtmParams object has at least one populated field.
 */
function hasUtmParams(params: UtmParams): boolean {
  return UTM_KEYS.some((key) => !!params[key]);
}

/**
 * Capture UTM parameters from the URL and store in sessionStorage.
 * Only stores on the FIRST visit in the session (won't overwrite existing).
 * Call once on app mount.
 */
export function captureUtmParams(): void {
  try {
    // Don't overwrite if already captured this session
    if (sessionStorage.getItem(SESSION_STORAGE_KEY)) return;

    const params = extractUtmFromUrl();
    if (hasUtmParams(params)) {
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(params));

      if (import.meta.env.DEV) {
        console.debug('[UTM] Captured params:', params);
      }
    }
  } catch {
    // sessionStorage unavailable — fail silently
  }
}

/**
 * Retrieve stored UTM parameters from sessionStorage.
 * Returns null if none were captured.
 */
export function getStoredUtmParams(): UtmParams | null {
  try {
    const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as UtmParams;
  } catch {
    return null;
  }
}

/**
 * Get the GA4 client ID from the _ga cookie.
 * GA4 stores the client ID in a cookie named _ga with format: GA1.1.XXXXXXXXXX.XXXXXXXXXX
 * The client ID is the last two dot-separated segments.
 * Returns null if the cookie doesn't exist (consent denied or analytics not loaded).
 */
export function getGA4ClientId(): string | null {
  try {
    const match = document.cookie.match(/(?:^|;\s*)_ga=GA\d+\.\d+\.(.+?)(?:;|$)/);
    if (match && match[1]) {
      return match[1]; // e.g. "123456789.1234567890"
    }
    return null;
  } catch {
    return null;
  }
}
